import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReportDataService from '../../services/report.service';
import { useTranslation } from '../../hooks/useTranslation';
import { formatCurrencySync } from '../../utils/currencyUtils';

const EarningsByBatchDashboard = () => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await ReportDataService.getEarningsByBatch();
      if (response.data && response.data.data) {
        setData(response.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || t('reports.errorLoading'));
      console.error('Error loading earnings by batch:', err);
    } finally {
      setLoading(false);
    }
  };

  const getTotalProfit = () => {
    return data.reduce((sum, item) => sum + (item.profit || 0), 0);
  };

  const getTotalRevenue = () => {
    return data.reduce((sum, item) => sum + (item.revenue || 0), 0);
  };

  const getTotalCost = () => {
    return data.reduce((sum, item) => sum + (item.cost || 0), 0);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <TrendingUpIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h5" component="h2">
              {t('reports.earningsByBatch')}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {t('reports.earningsByBatchDescription')}
          </Typography>
        </CardContent>
      </Card>

      {data.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {t('reports.totalRevenue')}
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {formatCurrencySync(getTotalRevenue())}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {t('reports.totalCost')}
                  </Typography>
                  <Typography variant="h4" color="error">
                    {formatCurrencySync(getTotalCost())}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {t('reports.totalProfit')}
                  </Typography>
                  <Typography variant="h4" color={getTotalProfit() >= 0 ? 'success.main' : 'error'}>
                    {formatCurrencySync(getTotalProfit())}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      <Card>
        <CardContent>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>{t('reports.product')}</strong></TableCell>
                  <TableCell><strong>{t('reports.buyDate')}</strong></TableCell>
                  <TableCell align="right"><strong>{t('reports.quantity')}</strong></TableCell>
                  <TableCell align="right"><strong>{t('reports.revenue')}</strong></TableCell>
                  <TableCell align="right"><strong>{t('reports.cost')}</strong></TableCell>
                  <TableCell align="right"><strong>{t('reports.profit')}</strong></TableCell>
                  <TableCell align="right"><strong>{t('reports.profitMargin')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {t('reports.noData')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((item, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{item.product_name}</TableCell>
                      <TableCell>{item.buy_date}</TableCell>
                      <TableCell align="right">
                        {parseFloat(item.quantity || 0).toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrencySync(item.revenue || 0)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrencySync(item.cost || 0)}
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={formatCurrencySync(item.profit || 0)}
                          size="small"
                          color={item.profit >= 0 ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {parseFloat(item.profit_margin || 0).toFixed(2)}%
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EarningsByBatchDashboard;

